import React from 'react';

import { Badge, Grid, Icon, Separator, Stack, Text } from '@eluve/components';

type DateStatus = 'active' | 'not_started' | 'expired';

const dateStatusConfig: Record<
  DateStatus,
  { label: string; variant: 'success' | 'info' | 'secondary' }
> = {
  active: { label: 'Active', variant: 'success' },
  not_started: { label: 'Not Started', variant: 'info' },
  expired: { label: 'Expired', variant: 'secondary' },
};

export type ExperimentMatch = {
  experimentId: string;
  experimentName: string;
  posthogFlagKey: string;
  variantId: string;
  variantName: string;
  posthogVariantKey: string;
  variantConfig: Record<string, unknown>;
  rank: number;
  isWinner: boolean;
  isInjected: boolean;
  dateStatus: DateStatus;
};

interface ExperimentMatchCardProps {
  match: ExperimentMatch;
}

export const ExperimentMatchCard: React.FC<ExperimentMatchCardProps> = ({
  match,
}) => {
  const cardClasses = match.isWinner
    ? 'border-border-positive-subtle bg-bg-positive-subtle'
    : 'border-border-primary bg-bg-gray-subtle';

  return (
    <div className={`w-full rounded-lg border p-3 ${cardClasses}`}>
      <Stack gap={2}>
        <Stack gap={2} align="center" direction="horizontal" wrap>
          {match.isWinner && (
            <Icon name="Trophy" className="text-text-positive" />
          )}
          <Text size="md" inline weight="semibold">
            {match.experimentName}
          </Text>
          <Badge variant={match.isWinner ? 'success' : 'secondary'}>
            Rank: {match.rank}
          </Badge>
          <Badge variant={dateStatusConfig[match.dateStatus].variant}>
            {dateStatusConfig[match.dateStatus].label}
          </Badge>
          {match.isInjected && <Badge variant="info">Injected</Badge>}
          {match.isWinner && <Badge variant="success">Winner</Badge>}
        </Stack>

        <Separator className="my-2" />

        <Grid columns={3} gap={2}>
          <Stack>
            <Text size="xs" color="tertiary">
              Variant
            </Text>
            <Text size="xs">{match.variantName}</Text>
          </Stack>
          <Stack>
            <Text size="xs" color="tertiary">
              PostHog Key
            </Text>
            <Text size="xs" className="font-mono">
              {match.posthogVariantKey}
            </Text>
          </Stack>
          <Stack>
            <Text size="xs" color="tertiary">
              Flag Key
            </Text>
            <Text size="xs" className="font-mono">
              {match.posthogFlagKey}
            </Text>
          </Stack>
        </Grid>

        {Object.keys(match.variantConfig).length > 0 && (
          <Stack gap={1}>
            <Text size="xs" color="tertiary">
              Parsed Config
            </Text>
            <pre className="overflow-x-auto rounded-sm bg-bg-gray-subtle p-2 text-xs">
              {JSON.stringify(match.variantConfig, null, 2)}
            </pre>
          </Stack>
        )}
      </Stack>
    </div>
  );
};
